import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ChevronLeft, ChevronRight, Send, Plus } from 'lucide-react'
import { AiSparkle } from '@/components/AiSparkle'
import { Button } from '@/components/ui/button'
import { RichTextEditor } from '@/components/ui/rich-text-editor'
import { useToast } from '@/hooks/use-toast'
import { formatDateForLocale } from '@/lib/dateUtils'
import api from '@/lib/api/axios'
import { sanitizeRichText } from '@/lib/sanitize-html'
import { useCan } from '@/hooks/useCan'

interface ManagementUpdate {
    id: number
    datetime: string | null
    userId: string | null
    userName: string | null
    content: string
}

interface ManagementUpdatesTabProps {
    setId: number
    setName?: string
}

export function ManagementUpdatesTab({ setId, setName }: ManagementUpdatesTabProps) {
    const { t, i18n } = useTranslation()
    const { toast } = useToast()
    const queryClient = useQueryClient()
    const [editorContent, setEditorContent] = useState('')
    const [isComposing, setIsComposing] = useState(false)
    const [currentIndex, setCurrentIndex] = useState(0)
    const [isGenerating, setIsGenerating] = useState(false)
    // Writing an update for a recovery set is managing it: recover-requests.update. Reading is for all.
    const canManage = useCan('recover-requests.update')

    const { data: updates = [], isLoading } = useQuery({
        queryKey: ['management-updates', setId],
        queryFn: async () => {
            const res = await api.get(`/api/recovery-sets/${setId}/management-updates`)
            return (res.data as any)?.data || []
        },
        enabled: !!setId,
    })

    const saveMutation = useMutation({
        mutationFn: async (content: string) => {
            const res = await api.post(`/api/recovery-sets/${setId}/management-updates`, { content })
            return res.data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['management-updates', setId] })
            setEditorContent('')
            setIsComposing(false)
            setCurrentIndex(0)
            toast({ title: 'Saved', description: 'Management update saved successfully' })
        },
        onError: () => {
            toast({ title: 'Error', description: 'Failed to save update', variant: 'destructive' })
        },
    })

    const handleSave = useCallback(() => {
        if (!editorContent.trim() || editorContent === '<p></p>') return
        saveMutation.mutate(editorContent)
    }, [editorContent, saveMutation])

    const handleGenerate = useCallback(async () => {
        setIsGenerating(true)
        try {
            const res = await api.post(`/api/recovery-sets/${setId}/management-updates/generate`)
            const html = (res.data as any)?.data?.html || ''
            if (html) {
                setEditorContent(html)
                setIsComposing(true)
            }
        } catch {
            toast({ title: 'Error', description: 'Failed to generate update', variant: 'destructive' })
        } finally {
            setIsGenerating(false)
        }
    }, [setId, toast])

    const currentUpdate = updates[currentIndex] as ManagementUpdate | undefined

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-sm font-semibold">{t('recoverRequests.managementUpdates', 'Management Updates')}</h3>
                    {setName && <p className="text-xs text-muted-foreground">{setName}</p>}
                </div>
                {canManage && (
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            className="gap-1.5"
                            onClick={handleGenerate}
                            disabled={isGenerating}
                        >
                            {isGenerating ? (
                                <img src="/images/vs-spinner.png" alt="" className="h-5 w-5 animate-spin" />
                            ) : (
                                <AiSparkle className="h-5 w-5 text-blue-500" />
                            )}
                            {t('recoverRequests.generateUpdate', 'AI Generate')}
                        </Button>
                        <Button size="sm" className="gap-1.5" onClick={() => { setIsComposing(true); setEditorContent('') }}>
                            <Plus className="h-3.5 w-3.5" />
                            {t('recoverRequests.newUpdate', 'New Update')}
                        </Button>
                    </div>
                )}
            </div>

            {/* Compose area */}
            {isComposing && (
                <div className="rounded-lg border bg-card p-4 space-y-3">
                    <RichTextEditor
                        value={editorContent}
                        onChange={setEditorContent}
                        placeholder="Enter management update..."
                        minHeight={150}
                    />
                    <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={() => { setIsComposing(false); setEditorContent('') }}>
                            {t('common.cancel', 'Cancel')}
                        </Button>
                        <Button size="sm" className="gap-1.5" onClick={handleSave} disabled={saveMutation.isPending}>
                            <Send className="h-3.5 w-3.5" />
                            {t('recoverRequests.saveUpdate', 'Save Update')}
                        </Button>
                    </div>
                </div>
            )}

            {/* Previous updates browser */}
            {isLoading ? (
                <div className="rounded-lg border p-8 text-center text-sm text-muted-foreground">
                    {t('common.loading', 'Loading...')}
                </div>
            ) : updates.length === 0 && !isComposing ? (
                <div className="rounded-lg border p-8 text-center text-sm text-muted-foreground">
                    {t('recoverRequests.noManagementUpdates', 'No management updates yet')}
                </div>
            ) : updates.length > 0 ? (
                <div className="rounded-lg border bg-card">
                    {/* Navigation header */}
                    <div className="flex items-center justify-between px-4 py-2 border-b bg-muted/30">
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            disabled={currentIndex >= updates.length - 1}
                            onClick={() => setCurrentIndex(i => Math.min(i + 1, updates.length - 1))}
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <span className="text-xs text-muted-foreground">
                            {currentIndex + 1} of {updates.length}
                            {currentUpdate?.datetime && (
                                <> — {formatDateForLocale(currentUpdate.datetime, i18n.language, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</>
                            )}
                            {currentUpdate?.userName && (
                                <> by {currentUpdate.userName}</>
                            )}
                        </span>
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            disabled={currentIndex <= 0}
                            onClick={() => setCurrentIndex(i => Math.max(i - 1, 0))}
                        >
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    </div>
                    {/* Content */}
                    {currentUpdate && (
                        <div
                            className="p-4 prose prose-sm dark:prose-invert max-w-none text-sm"
                            dangerouslySetInnerHTML={{ __html: sanitizeRichText(currentUpdate.content) }}
                        />
                    )}
                </div>
            ) : null}
        </div>
    )
}
